import type { WorkspaceDefinition, WorkspaceEnvironment, WorkspaceExpression, WorkspaceRows } from './workspaceTypes'

export function workspaceRows(value: unknown): WorkspaceRows {
    return Array.isArray(value) ? value.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r)) : []
}
function path(value: unknown, key: string): unknown {
    return key.split('.').filter(Boolean).reduce<unknown>((v, part) => {
        if (part === '__proto__' || part === 'constructor' || part === 'prototype') { return undefined }
        return v && typeof v === 'object' ? (v as Record<string, unknown>)[part] : undefined
    }, value)
}
/** Small closed expression vocabulary: no scripts, eval, HTML, or network execution. */
export function workspaceValue(expression: WorkspaceExpression, env: WorkspaceEnvironment, depth = 0): unknown {
    if (depth > 32) { throw new Error('Workspace expression exceeds the nesting limit.') }
    if (typeof expression === 'string') { return expression.startsWith('$') ? path(env, expression.slice(1)) : expression }
    if (expression === null || typeof expression !== 'object' || Array.isArray(expression)) { return expression }
    const e = expression as { op?: string; args?: unknown[] }
    if (!e.op) { return expression }
    const args = e.args ?? []
    const read = (i: number) => workspaceValue(args[i], env, depth + 1)
    const values = () => args.map((_, i) => read(i))
    switch (e.op) {
        case 'literal': return args[0]
        case 'get': return path(read(0), String(read(1) ?? ''))
        case 'if': return read(0) ? read(1) : read(2)
        case 'coalesce': return values().find(v => v !== null && v !== undefined && v !== '') ?? null
        case 'eq': return read(0) === read(1)
        case 'ne': return read(0) !== read(1)
        case 'and': return args.every((_, i) => !!read(i))
        case 'or': return args.some((_, i) => !!read(i))
        case 'not': return !read(0)
        case 'lt': return read(0) != null && read(1) != null && Number(read(0)) < Number(read(1))
        case 'lte': return read(0) != null && read(1) != null && Number(read(0)) <= Number(read(1))
        case 'gte': return read(0) != null && read(1) != null && Number(read(0)) >= Number(read(1))
        case 'count': return Array.isArray(read(0)) ? (read(0) as unknown[]).length : 0
        case 'first': return Array.isArray(read(0)) ? (read(0) as unknown[])[0] : null
        case 'concat': return values().map(v => v == null ? '' : String(v)).join('')
        case 'concatRows': return values().flatMap(workspaceRows)
        case 'upper': return String(read(0) ?? '').toUpperCase()
        case 'lower': return String(read(0) ?? '').toLowerCase()
        case 'contains': return Array.isArray(read(0)) ? (read(0) as unknown[]).includes(read(1)) : String(read(0) ?? '').includes(String(read(1) ?? ''))
        case 'join': return Array.isArray(read(0)) ? (read(0) as unknown[]).filter(v => v != null && v !== '').join(String(read(1) ?? ', ')) : ''
        case 'unique': return Array.isArray(read(0)) ? [...new Set(read(0) as unknown[])] : []
        case 'filter': return workspaceRows(read(0)).filter(row => workspaceValue(args[1], { ...env, row, parent: env.row }, depth + 1))
        case 'map': return workspaceRows(read(0)).map(row => workspaceValue(args[1], { ...env, row, parent: env.row }, depth + 1))
        case 'lookup': return workspaceRows(read(0)).find(row => path(row, String(read(1))) === read(2)) ?? null
        case 'sort': return [...workspaceRows(read(0))].sort((a, b) => String(path(a, String(read(1))) ?? '').localeCompare(String(path(b, String(read(1))) ?? ''), undefined, { numeric: true }) * (read(2) === 'desc' ? -1 : 1))
        case 'latest': {
            const grouped = new Map<unknown, Record<string, unknown>>()
            const key = String(read(1)); const revision = String(read(2))
            const preferred = read(3)
            for (const row of workspaceRows(read(0))) {
                const group = path(row, key) ?? row.id
                const previous = grouped.get(group)
                const rank = typeof preferred === 'string' && path(row, preferred) === true ? 1 : 0
                const previousRank = typeof preferred === 'string' && path(previous, preferred) === true ? 1 : 0
                if (!previous || rank > previousRank || (rank === previousRank && Number(path(row, revision) ?? 0) > Number(path(previous, revision) ?? 0))) { grouped.set(group, row) }
            }
            return [...grouped.values()]
        }
        case 'days': {
            const date = read(0)
            if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(date)) { return null }
            const delta = (Date.parse(date.slice(0, 10) + 'T00:00:00Z') - Date.parse(env.today + 'T00:00:00Z')) / 86400000
            return Number.isFinite(delta) ? delta : null
        }
        case 'ratio': return Number(read(1)) > 0 ? Math.round(Number(read(0)) / Number(read(1)) * 100) : null
        case 'subtract': return read(0) != null && read(1) != null ? Math.round((Number(read(0)) - Number(read(1))) * 100) / 100 : null
        default: throw new Error(`Unsupported workspace expression: ${e.op}`)
    }
}

export function workspaceDatasets(definition: WorkspaceDefinition, environment: WorkspaceEnvironment): Record<string, unknown> {
    const data = { ...environment.data }
    for (const dataset of definition.datasets ?? []) {
        const env = { ...environment, data }
        data[dataset.key] = workspaceRows(workspaceValue(dataset.rows, env)).map(original => {
            const row = { ...original }
            for (const [key, expression] of Object.entries(dataset.fields ?? {})) {
                row[key] = workspaceValue(expression, { ...env, row })
            }
            return row
        })
    }
    return data
}

export function safeWorkspaceHref(value: unknown): string | undefined {
    if (typeof value !== 'string' || [...value].some(char => char.charCodeAt(0) <= 32 || char === '\\')) { return undefined }
    if (value.startsWith('/app/detail/') && !/^\/app\/detail\/[^/]+\/[^/]+$/.test(value)) { return undefined }
    if ((value === '/users' || value === '/app' || value.startsWith('/app/') || value.startsWith('/settings/')) && !value.startsWith('//') && !/\/(?:undefined|null)(?:\/|$)/.test(value)) { return value }
    try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : undefined } catch { return undefined }
}
